import { EntityStatusChip } from '../../../../components/entity-detail/EntityStatusChip.js';
import React, { useEffect, useState } from 'react';
import { Card, Checkbox, Progress, Button, Space } from 'antd';
import { Maximize2, ListTodo } from 'lucide-react';
import { useTranslation } from '@govard/i18n-core/react';

export interface TodoItem {
  id: string;
  label: string;
  done: boolean;
  priority?: 'high' | 'medium' | 'low';
}

export interface AgentTodoSnippetProps {
  title?: string | undefined;
  items: TodoItem[];
  onToggleItem?: ((id: string) => void) | undefined;
  onViewCanvas?: (() => void) | undefined;
}

export const AgentTodoSnippet: React.FC<AgentTodoSnippetProps> = ({
  title,
  items: initialItems,
  onToggleItem,
  onViewCanvas,
}) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<TodoItem[]>(initialItems);

  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  const handleToggle = (id: string, checked: boolean) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, done: checked } : item)));
    if (onToggleItem) {
      onToggleItem(id);
    }
  };

  const completedCount = items.filter((i) => i.done).length;
  const percent = items.length > 0 ? Math.round((completedCount / items.length) * 100) : 0;

  return (
    <Card
      size="small"
      style={{
        borderRadius: 12,
        borderColor: 'var(--ant-color-border)',
        background: 'var(--g-fill)',
      }}
      styles={{ body: { padding: '12px 16px' } }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Space size={8}>
            <ListTodo size={16} style={{ color: 'var(--ant-color-primary)' }} />
            <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--color-text)' }}>
              {title ?? t('crm.ai_chat.todo.default_title')}
            </span>
          </Space>
          {onViewCanvas && (
            <Button
              type="text"
              size="small"
              icon={<Maximize2 size={13} />}
              onClick={onViewCanvas}
              style={{ fontSize: 11, color: 'var(--ant-color-text-secondary)' }}
            >
              {t('crm.ai_chat.common.open_canvas')}
            </Button>
          )}
        </div>
      }
    >
      <div style={{ marginBottom: 10 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 11,
            color: 'var(--ant-color-text-secondary)',
            marginBottom: 4,
          }}
        >
          <span>{t('crm.ai_chat.todo.progress')}</span>
          <span
            style={{
              fontWeight: 600,
              color: percent === 100 ? 'var(--ant-color-success)' : 'var(--ant-color-primary)',
            }}
          >
            {t('crm.ai_chat.todo.completed_count', {
              completed: completedCount,
              total: items.length,
              percent,
            })}
          </span>
        </div>
        <Progress
          percent={percent}
          showInfo={false}
          strokeColor={percent === 100 ? 'var(--ant-color-success)' : 'var(--ant-color-primary)'}
          railColor="var(--ant-color-border-secondary, #e7e5e4)"
          size="small"
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((item) => (
          <div
            key={item.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 10px',
              borderRadius: 6,
              background: item.done ? 'var(--g-fill)' : 'var(--ant-color-bg-container)',
              border: '1px solid var(--ant-color-border)',
              transition: 'all 0.15s ease',
            }}
          >
            <Checkbox
              checked={item.done}
              onChange={(event) => handleToggle(item.id, event.target.checked)}
              style={{
                fontSize: 12,
                textDecoration: item.done ? 'line-through' : 'none',
                color: item.done ? 'var(--ant-color-text-tertiary)' : 'var(--color-text)',
              }}
            >
              {item.label}
            </Checkbox>

            {item.priority && (
              <EntityStatusChip
                tone={
                  item.priority === 'high'
                    ? 'error'
                    : item.priority === 'medium'
                      ? 'warning'
                      : 'default'
                }
                style={{ fontSize: 10, margin: 0, padding: '0 4px', lineHeight: '16px' }}
              >
                {item.priority.toUpperCase()}
              </EntityStatusChip>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
};
